import { useState } from 'react';

import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import useReports from 'api/useReports';
import { useUserDetails } from 'api/useUserDetails';
import { EQueryKeys } from 'constants/enums';
import { getSelectProjectOptions } from 'utils/projectsUtil';
import { EToasterStatus, showToast } from 'utils/showToast';

import GenerateReportModal from './Components/GenerateReportModal';
import Header from './Components/Header';
import { ReportsFilterBar } from './Components/ReportsFilterBar';
import Tabs from './Components/Tabs';
import { useReportsFilters } from './hooks';
import { Interval } from './types/report.types';

import '../view.scss';

export enum EReportsTabs {
  FINANCIAL = 'financialreports',
  PAYOUTS = 'payouts'
}

const ReportsView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const basePath = location.pathname.split('/reports')[0];
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    selectedProjects,
    handleProjectChange,
    startDate,
    endDate,
    handleDateChange,
    clearFiltersButton,
    handleClearFilters
  } = useReportsFilters();

  const { userDetails } = useUserDetails();
  const projectsDetails = userDetails?.projects;

  const { createReport } = useReports(
    projectsDetails && projectsDetails.length > 0
      ? getSelectProjectOptions(projectsDetails, [])[0]?.value
      : undefined
  );

  const getCurrentTab = () => {
    const path = location.pathname;
    if (path.includes(EReportsTabs.PAYOUTS)) return EReportsTabs.PAYOUTS;
    return EReportsTabs.FINANCIAL;
  };

  const handleTabChange = (value: EReportsTabs) => {
    navigate(`${basePath}/reports/${value}${location.search}`);
  };

  const handleGenerateReport = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleGenerateReportSubmit = (
    startDate: string,
    endDate: string,
    projectIds: string[]
  ) => {
    if (!startDate || !endDate) {
      showToast({
        message: 'Please select a date range',
        status: EToasterStatus.ERROR
      });
      return;
    }

    // Date strings are in YYYY-MM-DD format, append time for start and end of day
    const data: {
      interval: Interval;
      createdBy: string;
    } = {
      interval: {
        startDate: `${startDate}T00:00:00.000Z`,
        endDate: `${endDate}T23:59:59.999Z`
      },
      createdBy: `${userDetails?.firstName} ${userDetails?.lastName}`
    };

    const mutationParams: {
      data: { interval: Interval; createdBy: string };
      projects?: string;
    } = { data };

    if (projectIds.length > 0) {
      mutationParams.projects = projectIds.join(',');
    }

    createReport.mutate(mutationParams, {
      onSuccess: () => {
        // Invalidate and refetch the reports query to show the new report immediately
        queryClient.invalidateQueries({ queryKey: [EQueryKeys.FINANCIAL_REPORTS] });
        showToast({
          message: 'Your report request was successful.',
          status: EToasterStatus.SUCCESS
        });
        setIsModalOpen(false);
      },
      onError: () => {
        showToast({
          message: 'Report creation failed. Please try again later.',
          status: EToasterStatus.ERROR
        });
      }
    });
  };

  return (
    <div className="w-full flex flex-col gap-4 p-6 pt-4">
      <Header onGenerateReport={handleGenerateReport} />
      <Tabs activeTab={getCurrentTab()} onTabChange={handleTabChange} />
      {getCurrentTab() === EReportsTabs.FINANCIAL && (
        <ReportsFilterBar
          clearFiltersButton={clearFiltersButton}
          handleClearFilters={handleClearFilters}
          selectedProjects={selectedProjects}
          onProjectChange={handleProjectChange}
          startDate={startDate}
          endDate={endDate}
          onDateChange={handleDateChange}
        />
      )}
      <div className="flex-1 overflow-auto">
        <Outlet context={{ selectedProjects, startDate, endDate }} />
      </div>
      <GenerateReportModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onGenerate={handleGenerateReportSubmit}
      />
    </div>
  );
};

export default ReportsView;
